import { SECTION_MANIFEST } from "./section-manifest";
import type { SectionComponent } from "./types";

type SectionLoader = () => Promise<SectionComponent>;

type ResolvedLazySection = {
  load: SectionLoader;
  moduleId: string;
};

const SECTION_MODULES = import.meta.glob<Record<string, unknown>>([
  "/src/components/kit/*/*.tsx",
  "!/src/components/kit/**/*.stories.tsx",
]);

const SECTIONS = Object.fromEntries(
  Object.entries(SECTION_MANIFEST).flatMap(([type, def]) => {
    const lazy = def.lazy;
    if (!lazy) return [];

    const loadModule = SECTION_MODULES[`/${lazy.moduleId}`];
    if (!loadModule) {
      throw new Error(`Lazy section module not found: ${lazy.moduleId}`);
    }

    const load: SectionLoader = async () => {
      const module = await loadModule();
      const component = module[lazy.exportName];
      if (
        typeof component !== "function" &&
        (typeof component !== "object" || component === null)
      ) {
        throw new Error(`Lazy section export not found: ${lazy.exportName} in ${lazy.moduleId}`);
      }
      return component as SectionComponent;
    };

    return [[type, { load, moduleId: lazy.moduleId } satisfies ResolvedLazySection]];
  }),
) as Record<string, ResolvedLazySection>;

const resolved = new Map<string, SectionComponent>();

export function isLazySectionType(type: string): boolean {
  return type in SECTIONS;
}

export function getResolvedSectionComponent(type: string): SectionComponent | undefined {
  return resolved.get(type);
}

export function getSectionModuleId(type: string): string | undefined {
  return SECTIONS[type]?.moduleId;
}

export const LAZY_SECTION_TYPES: readonly string[] = Object.keys(SECTIONS);

export async function preloadSectionComponents(types: readonly string[]): Promise<void> {
  await Promise.all(
    types.map(async (type) => {
      const def = SECTIONS[type];
      if (!def || resolved.has(type)) return;
      resolved.set(type, await def.load());
    }),
  );
}

export function extractLazySectionTypes(config: unknown): string[] {
  if (!config || typeof config !== "object") return [];
  const sections = (config as { sections?: unknown }).sections;
  if (!Array.isArray(sections)) return [];
  const types = new Set<string>();
  for (const section of sections) {
    if (section && typeof section === "object") {
      const type = (section as { type?: unknown }).type;
      if (typeof type === "string" && isLazySectionType(type)) {
        types.add(type);
      }
    }
  }
  return [...types];
}
